import { useEffect, useState } from "react";
import { api } from "../../lib/api";

const fmt = (iso) => (iso ? iso.slice(0, 10) : "—");

const STATUS_DOT = {
  completed: "bg-emerald-400",
  in_progress: "bg-amber-400",
  not_started: "bg-neutral-700",
};

export const RMTrainingOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/rm-training/overview")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-sm text-neutral-500">Loading…</p>;
  if (!data) return <p className="text-sm text-neutral-500">Couldn't load the training overview.</p>;

  return (
    <div data-testid="admin-rm-training-overview">
      <p className="text-sm text-neutral-400">
        Each RM has {data.window_days} days from opening Module 1 to complete all {data.total_modules} modules.
        Dots show module status; numbers are quiz scores.
      </p>
      <div className="mt-4 overflow-x-auto rounded-2xl border border-neutral-800">
        <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-rm-training-table">
          <thead className="bg-neutral-900 text-[10px] uppercase tracking-wider text-neutral-400">
            <tr>
              <th className="px-3 py-3">RM</th>
              <th className="px-3 py-3">Current Module</th>
              <th className="px-3 py-3">Quiz Scores (M1–M9)</th>
              <th className="px-3 py-3 text-center">Completion</th>
              <th className="px-3 py-3">Started</th>
              <th className="px-3 py-3 text-center">Days Left</th>
              <th className="px-3 py-3">Flags</th>
              <th className="px-3 py-3">Certification</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800 bg-neutral-950">
            {data.rows.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-500">No RM accounts yet.</td></tr>
            ) : data.rows.map((r) => (
              <tr key={r.rm_id} data-testid={`admin-rm-training-row-${r.rm_id}`}>
                <td className="px-3 py-3">
                  <p className="font-semibold text-neutral-100">{r.name}</p>
                  <p className="text-[11px] text-neutral-500">{r.email}</p>
                </td>
                <td className="px-3 py-3 text-xs text-neutral-300" data-testid={`admin-rm-training-current-${r.rm_id}`}>
                  {r.current_module
                    ? <>M{r.current_module} · <span className="text-neutral-400">{r.current_module_title}</span></>
                    : <span className="font-bold text-emerald-400">All completed</span>}
                </td>
                <td className="px-3 py-3">
                  <div className="flex gap-1.5">
                    {Array.from({ length: data.total_modules }, (_, i) => {
                      const mid = String(i + 1);
                      const st = r.module_statuses[mid] || "not_started";
                      const score = r.quiz_scores[mid];
                      return (
                        <span key={mid} title={`M${mid}: ${st}${score != null ? ` · ${score}%` : ""}`}
                          className="flex h-7 w-7 items-center justify-center rounded-md border border-neutral-800 text-[10px] font-bold text-neutral-300">
                          {score != null ? score : <span className={`h-2 w-2 rounded-full ${STATUS_DOT[st]}`} />}
                        </span>
                      );
                    })}
                  </div>
                </td>
                <td className="px-3 py-3 text-center">
                  <span className="font-bold text-[#2dd4bf]" data-testid={`admin-rm-training-pct-${r.rm_id}`}>{r.completion_pct}%</span>
                  <p className="text-[10px] text-neutral-500">{r.completed_count}/{data.total_modules}</p>
                </td>
                <td className="px-3 py-3 text-xs text-neutral-300">{fmt(r.started_at)}</td>
                <td className="px-3 py-3 text-center">
                  {r.days_remaining == null ? <span className="text-neutral-600">—</span> : (
                    <span className={`font-bold ${r.days_remaining <= 5 ? "text-red-400" : "text-neutral-200"}`}>
                      {r.days_remaining}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3" data-testid={`admin-rm-training-flags-${r.rm_id}`}>
                  {(r.flagged_modules || []).length === 0 && !r.final_flagged ? (
                    <span className="text-neutral-600">—</span>
                  ) : (
                    <div className="space-y-1">
                      {(r.flagged_modules || []).map((mid) => (
                        <span key={mid} className="block w-fit rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-bold text-red-400">
                          M{mid} · 2 failed attempts
                        </span>
                      ))}
                      {r.final_flagged && (
                        <span className="block w-fit rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] font-bold text-red-400">
                          Final · 2 failed attempts
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3" data-testid={`admin-rm-training-cert-${r.rm_id}`}>
                  {r.certification ? (
                    <div>
                      <span className="rounded-full bg-[#0A8B85]/15 px-2 py-0.5 text-[10px] font-bold text-[#2dd4bf]">Certified</span>
                      <p className="mt-1 text-[10px] text-neutral-400">
                        {fmt(r.certification.certified_at)} · {r.certification.score_pct}%
                      </p>
                    </div>
                  ) : (
                    <span className="text-neutral-600">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
